/**
 * Thin wrappers around the treasurer-module Cloud Functions. Each wrapper
 * narrows the input/output type, throws a tidy `Error` on failure, and
 * keeps the call-site free of `httpsCallable<unknown, unknown>` noise.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

export interface SetupTreasurerInput {
  unitId: string;
  treasurerName?: string;
  treasurerEmail?: string;
  worshipfulMasterName?: string;
  worshipfulMasterEmail?: string;
  worshipfulMasterUserId?: string | null;
  financialYearStart?: { month: number; day: number };
  subscriptionDueDate?: { month: number; day: number };
  grandLodgeCapitationRatePence?: number;
  largeTransactionThresholdPence?: number;
  meetingDates?: string[];
  prorateSubscriptionsForMidYearLeavers?: boolean;
}

export async function setupTreasurerModule(
  input: SetupTreasurerInput,
): Promise<{ success: boolean; initialised: boolean }> {
  const fn = httpsCallable<SetupTreasurerInput, { success: boolean; initialised: boolean }>(
    functions,
    'setupTreasurerModule',
  );
  const res = await fn(input);
  return res.data;
}

export interface MemberInputBase {
  unitId: string;
  firstName?: string;
  lastName?: string;
  email?: string | null;
  phone?: string | null;
  membershipNumber?: string | null;
  status?: 'active' | 'honorary' | 'joining' | 'resigned' | 'deceased' | 'suspended';
  joinedDate?: string;
  leftDate?: string | null;
  annualSubscriptionPence?: number;
  capitationExempt?: boolean;
  paymentReference?: string;
  notes?: string | null;
  linkedUserId?: string | null;
}

export type CreateMemberInput = MemberInputBase & {
  firstName: string;
  lastName: string;
  joinedDate: string;
  annualSubscriptionPence: number;
};

export type UpdateMemberInput = MemberInputBase & { memberId: string };

export interface MemberMutationResult {
  success: boolean;
  memberId: string;
  hasReferenceCollision: boolean;
  collidesWithMemberIds: string[];
}

export async function createTreasurerMember(input: CreateMemberInput): Promise<MemberMutationResult> {
  const fn = httpsCallable<CreateMemberInput, MemberMutationResult>(functions, 'createTreasurerMember');
  return (await fn(input)).data;
}

export async function updateTreasurerMember(input: UpdateMemberInput): Promise<MemberMutationResult> {
  const fn = httpsCallable<UpdateMemberInput, MemberMutationResult>(functions, 'updateTreasurerMember');
  return (await fn(input)).data;
}

export async function archiveTreasurerMember(
  input: { unitId: string; memberId: string; reason?: string },
): Promise<{ success: boolean; memberId: string }> {
  const fn = httpsCallable<typeof input, { success: boolean; memberId: string }>(
    functions,
    'archiveTreasurerMember',
  );
  return (await fn(input)).data;
}

export interface TreasurerRosterRow {
  linkedUserId: string | null;
  treasurerMemberId: string | null;
  firstName: string;
  lastName: string;
  email: string | null;
  membershipNumber: string | null;
  status: string;
  annualSubscriptionPence: number;
  paymentReference: string;
  hasReferenceCollision: boolean;
  needsTreasurerRecord: boolean;
  isArchived: boolean;
}

export async function listTreasurerRoster(
  input: { unitId: string },
): Promise<{ members: TreasurerRosterRow[] }> {
  const fn = httpsCallable<{ unitId: string }, { members: TreasurerRosterRow[] }>(
    functions,
    'listTreasurerRoster',
  );
  return (await fn(input)).data;
}

export interface FundCreateInput {
  unitId: string;
  name: string;
  description?: string;
  isCharity?: boolean;
}

export interface FundUpdateInput {
  unitId: string;
  fundId: string;
  name?: string;
  description?: string | null;
  isCharity?: boolean;
  isActive?: boolean;
}

export async function createTreasurerFund(input: FundCreateInput): Promise<{ success: boolean; fundId: string }> {
  const fn = httpsCallable<FundCreateInput, { success: boolean; fundId: string }>(
    functions,
    'createTreasurerFund',
  );
  return (await fn(input)).data;
}

export async function updateTreasurerFund(input: FundUpdateInput): Promise<{ success: boolean; fundId: string }> {
  const fn = httpsCallable<FundUpdateInput, { success: boolean; fundId: string }>(
    functions,
    'updateTreasurerFund',
  );
  return (await fn(input)).data;
}

export interface CategoryCreateInput {
  unitId: string;
  name: string;
  type: 'income' | 'expenditure';
  fundId: string;
}

export interface CategoryUpdateInput {
  unitId: string;
  categoryId: string;
  name?: string;
  type?: 'income' | 'expenditure';
  fundId?: string;
  isActive?: boolean;
}

export async function createTreasurerCategory(
  input: CategoryCreateInput,
): Promise<{ success: boolean; categoryId: string }> {
  const fn = httpsCallable<CategoryCreateInput, { success: boolean; categoryId: string }>(
    functions,
    'createTreasurerCategory',
  );
  return (await fn(input)).data;
}

export async function updateTreasurerCategory(
  input: CategoryUpdateInput,
): Promise<{ success: boolean; categoryId: string }> {
  const fn = httpsCallable<CategoryUpdateInput, { success: boolean; categoryId: string }>(
    functions,
    'updateTreasurerCategory',
  );
  return (await fn(input)).data;
}
